import type { SeriesOption, XAXisComponentOption, YAXisComponentOption } from 'echarts';

import type {
  TimeRangeLane,
  TimeSeries,
  TimeSeriesChartHighlight,
} from '@/shared/charts/time-series-chart/TimeSeriesChart.types';
import type { TimeSeriesChartTheme } from '@/shared/charts/time-series-chart/theme/TimeSeriesChartTheme.types';

import { PLOT_GRID_INDEX, PLOT_X_AXIS_INDEX } from '../config/chartLayout';
import { guideLineVisuals, plotVisuals } from '../config/chartVisuals';
import { dateTimeFormatter, timeFormatter } from '../config/timeFormatters';
import { buildSliderPreviewOption } from '../navigation/buildSliderPreviewOption';
import type { TimeDomain } from '../utils/timeDomain';
import { buildLaneHighlightOption } from './buildLaneHighlightOption';
import { buildLineSeriesOption } from './buildLineSeriesOption';
import { buildLonePointsOption } from './buildLonePointsOption';
import { getLineBucketCount } from './level-of-detail/getLineBucketCount';
import { getSymbolSeriesIds } from './level-of-detail/getSymbolSeriesIds';
import type { LineViewport } from './level-of-detail/lineViewport';

export type PlotOption = {
  xAxis: XAXisComponentOption;
  yAxis: YAXisComponentOption[];
  series: SeriesOption[];
};

type BuildPlotOptionParams = {
  series: TimeSeries[];
  highlightedLanes: TimeRangeLane[];
  highlight?: TimeSeriesChartHighlight;
  domain: TimeDomain;
  isGuideLineEnabled: boolean;
  viewport: LineViewport;
  previewSeriesId: string | undefined;
  /** Only with a slider on screen is there anything to preview, so the hidden series is added only then. */
  hasSliderPreview: boolean;
  theme: TimeSeriesChartTheme;
};

function findPreviewSeries(series: TimeSeries[], previewSeriesId: string | undefined) {
  return previewSeriesId ? series.find(({ id }) => id === previewSeriesId) : undefined;
}

function buildGuideLineAxisPointer(theme: TimeSeriesChartTheme): XAXisComponentOption['axisPointer'] {
  return {
    show: true,
    type: 'line',
    snap: false,
    label: {
      show: true,
      formatter: ({ value }) => timeFormatter.format(Number(value)),
      backgroundColor: theme.guideLine.labelBackground,
      color: theme.guideLine.labelColor,
      fontFamily: theme.guideLine.labelFontFamily,
      fontWeight: theme.guideLine.labelFontWeight,
      fontSize: theme.guideLine.labelFontSize,
      lineHeight: theme.guideLine.labelLineHeight,
      borderRadius: theme.guideLine.labelBorderRadius,
      padding: [...guideLineVisuals.labelPadding],
      margin: guideLineVisuals.labelMargin,
    },
    lineStyle: {
      color: theme.guideLine.color,
      width: guideLineVisuals.lineWidth,
      type: guideLineVisuals.lineType,
    },
  };
}

/** Full date + time on every tick, as in the design ("aug. 30 02:00"), independent of zoom level. */
function buildPlotXAxis(
  [min, max]: TimeDomain,
  isGuideLineEnabled: boolean,
  theme: TimeSeriesChartTheme
): XAXisComponentOption {
  return {
    type: 'time',
    gridIndex: PLOT_GRID_INDEX,
    // Pinned, so the plot and the track below it always span exactly the same time.
    min,
    max,
    axisLabel: {
      formatter: (value: number) => dateTimeFormatter.format(value),
      hideOverlap: true,
      color: theme.axis.labelColor,
      margin: plotVisuals.axisLabelMargin,
    },
    axisLine: { show: false },
    axisTick: { show: false },
    ...(isGuideLineEnabled && {
      axisPointer: buildGuideLineAxisPointer(theme),
    }),
  };
}

/**
 * Every line has its own hidden Y axis, so none of them can draw the horizontal guides without
 * implying a scale. This extra axis carries the guides alone: fixed 0..1 range, no labels.
 */
function buildSplitLineYAxis(theme: TimeSeriesChartTheme): YAXisComponentOption {
  return {
    type: 'value',
    gridIndex: PLOT_GRID_INDEX,
    min: 0,
    max: 1,
    interval: 1 / plotVisuals.splitLineCount,
    axisLabel: { show: false },
    axisLine: { show: false },
    axisTick: { show: false },
    splitLine: {
      show: true,
      lineStyle: {
        color: theme.axis.splitLineColor,
        width: 1,
      },
    },
  };
}

/** Invisible 0..1 axis for series that only carry markAreas and have no values of their own. */
function buildCarrierYAxis(): YAXisComponentOption {
  return {
    type: 'value',
    gridIndex: PLOT_GRID_INDEX,
    min: 0,
    max: 1,
    show: false,
  };
}

export function buildPlotOption({
  series,
  highlightedLanes,
  highlight,
  domain,
  isGuideLineEnabled,
  viewport,
  previewSeriesId,
  hasSliderPreview,
  theme,
}: BuildPlotOptionParams): PlotOption {
  // Index 0 so the guides are behind every data series; the preview and line axes follow.
  const yAxis: YAXisComponentOption[] = [buildSplitLineYAxis(theme)];
  const splitLineYAxisIndex = 0;
  const plotSeries: SeriesOption[] = [];

  if (hasSliderPreview) {
    // First among the series on purpose: ECharts binds the slider's shadow to the first line
    // series it finds on the zoomed axis, once, at creation — see buildSliderPreviewOption.
    const sliderPreview = buildSliderPreviewOption({
      previewSeries: findPreviewSeries(series, previewSeriesId),
      yAxisIndex: yAxis.length,
      bucketCount: getLineBucketCount(viewport.plotWidthPx),
    });

    yAxis.push(sliderPreview.yAxis);
    plotSeries.push({ ...sliderPreview.series, xAxisIndex: PLOT_X_AXIS_INDEX });
  }

  const lines = buildLineSeriesOption({
    series,
    yAxisOffset: yAxis.length,
    highlightedSeriesIds: highlight?.seriesIds,
    viewport,
    // Decided across every line at once, because the symbol cap is shared by all of them.
    symbolSeriesIds: getSymbolSeriesIds(series, viewport),
    theme,
  });

  yAxis.push(...lines.yAxis.map((axis) => ({ ...axis, gridIndex: PLOT_GRID_INDEX, splitLine: { show: false } })));
  plotSeries.push(...lines.series.map((lineSeries) => ({ ...lineSeries, xAxisIndex: PLOT_X_AXIS_INDEX })));

  // Present whenever a line can break, even with no lone point in view, so panning does not change
  // the option's structure. It reuses the guides' fixed 0..1 axis: the markers carry their own height.
  if (series.some(({ gapThresholdMs }) => gapThresholdMs !== undefined)) {
    plotSeries.push({
      ...buildLonePointsOption(lines.lonePoints, splitLineYAxisIndex),
      xAxisIndex: PLOT_X_AXIS_INDEX,
    });
  }

  if (highlightedLanes.length > 0) {
    const carrierYAxisIndex = yAxis.length;

    yAxis.push(buildCarrierYAxis());
    plotSeries.push({
      ...buildLaneHighlightOption(highlightedLanes, carrierYAxisIndex, theme),
      xAxisIndex: PLOT_X_AXIS_INDEX,
    });
  }

  return {
    xAxis: buildPlotXAxis(domain, isGuideLineEnabled, theme),
    yAxis,
    series: plotSeries,
  };
}
